import {
  Authenticator,
  Button,
  Text,
  ThemeProvider,
  useAuthenticator,
  useTheme,
  View,
} from "@aws-amplify/ui-react";
import React from "react";
import { Header } from "./Header";
import { signIn, SignInInput, signUp, SignUpInput } from "aws-amplify/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { I18n } from "@aws-amplify/core";
import { containsUpperCase } from "../utils/helper";

export function LoginWindow() {
  const [queryParams] = useSearchParams();
  const otp = (queryParams.get("otp") ?? "t") === "t"; // Default OTP is on
  const email = queryParams.get("email") ?? "";
  const churchId = queryParams.get("churchId") ?? "";
  const createChurchEmail = queryParams.get("createChurchEmail") ?? "";
  const first_name = queryParams.get("first_name") ?? "";
  const last_name = queryParams.get("last-name") ?? "";
  const navigate = useNavigate();
  const { tokens } = useTheme();

  const customTheme = {
    name: "baptize-theme",
    tokens: {
      components: {
        button: {
          primary: {
            backgroundColor: { value: "#00169E" },
            color: { value: "#ffffff" },
            _hover: {
              backgroundColor: { value: "#002244" },
            },
          },
        },
      },
    },
  };

  const components = {
    SignIn: {
      Header() {
        return (
          <View
            style={{
              display: "flex",
              justifyContent: "center",
              width: "400px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {/* <Text
              width="100%"
              fontSize="1.125rem"
              fontWeight="600"
              color="neutral.100"
              textAlign="center"
            >
              Login to find a local church near you
              hosting baptisms!
            </Text> */}
          </View>
        );
      },
      Footer() {
        const { toForgotPassword } = useAuthenticator();
        return (
          <>
            <Button
              variation="link"
              size="small"
              onClick={() => {
                navigate(
                  otp
                    ? `/account/login?otp=f&churchId=${encodeURIComponent(churchId ?? "")}`
                    : `/account/login?otp=t&churchId=${encodeURIComponent(churchId ?? "")}`,
                );
                navigate(0); //Force page to reload
              }}
            >
              {otp ? "Sign with Password" : "Sign without Password"}
            </Button>
            <Button variation="link" size="small" onClick={toForgotPassword}>
              Forgot Password
            </Button>
          </>
        );
      },
    },
    SignUp: {
      Header() {
        return (
          <View
            style={{
              display: "flex",
              justifyContent: "center",
              width: "400px",
              marginLeft: "auto",
              marginRight: "auto",
              paddingTop: "1rem",
            }}
          >
            <Text
              width="100%"
              fontSize="1.1rem"
              fontWeight="600"
              color="neutral.100"
              textAlign="center"
            >
              Finalize your account below to find a local church near you
              hosting baptisms!
            </Text>
          </View>
        );
      },
    },
  };

  I18n.putVocabulariesForLanguage("en", {
    "Create Account": "Register",
  });

  return (
    <ThemeProvider theme={customTheme}>
      <Header />
      <View
        maxWidth="400px"
        width="100%"
        margin="0 auto"
        style={{ display: "flex", justifyContent: "center" }}
      >
        <Authenticator
          initialState={first_name || email ? "signUp" : "signIn"}
          components={components}
          loginMechanisms={["email"]}
          signUpAttributes={["family_name", "given_name"]}
          formFields={{
            signIn: {
              username: {
                placeholder: "Enter Your Email Here",
                isRequired: true,
                label: "Email",
                defaultValue: createChurchEmail,
              },
              password: {
                type: otp ? "hidden" : "password",
                label: "Password",
                labelHidden: otp,
                placeholder: "Enter Your Password Here",
                isRequired: !otp,
              },
            },
            signUp: {
              given_name: {
                order: 1,
                placeholder: "Enter Your First Name",
                isRequired: true,
                label: "First Name",
                defaultValue: first_name,
              },
              family_name: {
                order: 2,
                placeholder: "Enter Your Last Name",
                isRequired: true,
                label: "Last Name",
                defaultValue: last_name,
              },
              email: {
                order: 3,
                placeholder: "Enter Your Email",
                isRequired: true,
                label: "Email",
                defaultValue: email,
              },
              password: {
                type: "hidden",
                label: "Password",
                labelHidden: true,
              },
              confirm_password: {
                type: "hidden",
                label: "Confirm Password",
                labelHidden: true,
              },
            },
          }}
          services={{
            async handleSignIn(input: SignInInput) {
              const { username, password } = input;

              if (containsUpperCase(username)) {
                throw new Error("Email must not contain uppercase letters.");
              }

              return signIn({
                username: username,
                password: password,
                options: {
                  authFlowType: otp ? "USER_AUTH" : "USER_PASSWORD_AUTH",
                  preferredChallenge: "EMAIL_OTP",
                },
              });
            },
            async handleSignUp(input: SignUpInput) {
              return signUp({
                username: input.username,
                password: input.password,
                options: {
                  ...input.options,
                  autoSignIn: {
                    authFlowType: "USER_AUTH",
                    preferredChallenge: "EMAIL_OTP",
                  },
                  userAttributes: {
                    ...input.options?.userAttributes,
                    email: input.username,
                  },
                },
              });
            },
            async validateCustomSignUp(formData) {
              if (containsUpperCase(formData.email)) {
                return {
                  email: "Email must not contain uppercase letters.",
                };
              }
            },
          }}
        />
      </View>
      <View textAlign="center" padding={tokens.space.large}>
        <Text color={tokens.colors.neutral[80]}>
          &copy; 2025 Baptize America
        </Text>
      </View>
    </ThemeProvider>
  );
}
